import { useServerTime } from "$global-providers/server-time-provider/provider";
import { useQuerystring } from "$routes/_lib/use-querystring";
import { Box } from "@material-ui/core";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { SectionHeading } from "./_lib/section-heading";
import { CompactInvoiceRow } from "./invoice-card/invoice-card";
import { LoadOlderButton } from "./load-older-button";
import { partitionByOlderCutoff } from "./outstanding-partition";
import { PreviousFilters } from "./previous-filters";
import type { V2InvoiceCardProps } from "./tab-content-types";

interface Props {
  invoices: V2InvoiceCardProps[];
  fromDate?: string;
  toDate?: string;
  setFromDate: (value: string) => void;
  setToDate: (value: string) => void;
}

export const PreviousSection: FC<Props> = ({
  invoices,
  fromDate,
  toDate,
  setFromDate,
  setToDate,
}) => {
  const { invoiceId: deeplinkId } = useQuerystring() as { invoiceId?: string };
  const { getDisplayTime } = useServerTime();

  // When the user has applied a date filter, the cutoff gate would silently
  // hide matches outside the recent window. Skip the cutoff in that case so
  // the filter does what the user expects.
  const hasDateFilter = Boolean(fromDate || toDate);
  const { recent, older } = hasDateFilter
    ? { recent: invoices, older: [] as V2InvoiceCardProps[] }
    : partitionByOlderCutoff(invoices, getDisplayTime());

  // Auto-reveal older rows when arriving via a deeplink to one of them —
  // otherwise the user sees the URL hint but no matching row.
  const deeplinkIsOlder = Boolean(
    deeplinkId && older.some((i) => i.invoiceId === deeplinkId),
  );
  const [showOlder, setShowOlder] = useState<boolean>(deeplinkIsOlder);

  const visible = showOlder || hasDateFilter ? invoices : recent;

  return (
    <Box data-testid="previous-section">
      <SectionHeading>
        <FormattedMessage
          id="invoicing.v2.section.previous"
          defaultMessage="Previous invoices"
        />
      </SectionHeading>
      <PreviousFilters
        fromDate={fromDate}
        toDate={toDate}
        setFromDate={setFromDate}
        setToDate={setToDate}
      />
      <Box display="flex" flexDirection="column">
        {visible.map((invoice) => (
          <CompactInvoiceRow key={invoice.invoiceId} {...invoice} />
        ))}
      </Box>
      {!showOlder && !hasDateFilter && older.length > 0 && (
        <LoadOlderButton
          count={older.length}
          onClick={() => setShowOlder(true)}
        />
      )}
    </Box>
  );
};
